import type { FC } from 'react';
import { useEffect } from 'react';

import { DeviceInfo } from '@pp/core/src/deviceInfo/deviceInfo';
import { Logger } from '@pp/core/src/logger/Logger';
import { IFRAME_TYPE } from '@pp/core/src/store/slices/launcher/types';

import { launcherContextActions } from '../../context/actions';
import { useLauncherContext } from '../../context/LauncherContext';
import type { LauncherTableInfo } from '../../context/types';
import { LauncherCommunicator } from '../../controllers/LauncherCommunicationController';
import { OPERATOR_GAME_IDS_TO_HIDE } from '../../helpers/constants';
import { LauncherSession } from '../../session/LauncherSession';

/**
 * 1. Remove MTB from the session tables.
 * 2. If lobby, update the url's hiddenGames param
 */
const ProcessSessionTables = (sessionTables: LauncherTableInfo[]): LauncherTableInfo[] => {
  const sessionTablesWithoutMtb = sessionTables.filter((table) => table.tableId !== 'MTB');
  return sessionTablesWithoutMtb.map((table) => {
    if (table.iframeType === IFRAME_TYPE.LOBBY) {
      const lobbyURL = new URL(table.url);
      const hiddenGamesOperatorIds = sessionTablesWithoutMtb
        .map((table) => table.operatorGameId)
        .concat(OPERATOR_GAME_IDS_TO_HIDE)
        .join(',');
      lobbyURL.searchParams.set('hiddenGames', hiddenGamesOperatorIds);
      return {
        ...table,
        url: lobbyURL.toString(),
      };
    }
    return table;
  });
};

export const LauncherInitialiser: FC = (): null => {
  const launcherContext = useLauncherContext();
  const { dispatch, tables } = launcherContext;

  useEffect(() => {
    DeviceInfo.init();
  }, []);

  useEffect(() => {
    const session = LauncherSession.getSession();
    if (session) {
      try {
        const sessionTables = JSON.parse(session?.tables) as LauncherTableInfo[];
        const validSessionTables = ProcessSessionTables(sessionTables);
        dispatch(
          launcherContextActions.setTables({
            tables: validSessionTables,
          })
        );
        Logger.logEvent('launcher', { additional: 'load' });
      } catch (e) {
        console.error('Error parsing session tables', e);
        Logger.logEvent('launcher', { additional: 'fail' });
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (tables.length === 0) return;
    LauncherCommunicator.dispatchTablesUpdatedMessage(tables);
    LauncherSession.updateSession(JSON.stringify(tables));
  }, [tables]);

  useEffect(() => {
    LauncherCommunicator.startListening();
    return (): void => {
      LauncherCommunicator.stopListening();
    };
  }, []);

  useEffect(() => {
    LauncherCommunicator.setStore(launcherContext);
  }, [launcherContext]);

  return null;
};
